/* eslint-disable no-bitwise */
/* eslint-disable no-param-reassign */
/* eslint-disable no-useless-escape */
import { navigate } from '@reach/router';
import { pathToRegexp } from 'path-to-regexp';
import moment from '@/moment';
import authHelpers from '@/services/auth/helpers';
import jwtDecode from 'jwt-decode';
import env, { SiteId } from '@/env';
import { AuthUser, Route } from '@/types';
import logoBankApi from '@/assets/images/bank-api-logo.svg';
import logoBankApiNotext from '@/assets/images/bank-api-logo-notext.svg';
import logoApiCafe from '@/assets/images/apicafe-logo.svg';
import logoApiCafeNotext from '@/assets/images/apicafe-logo-notext.svg';
import logo192BankApi from '@/assets/images/logo192-bankapi.png';
import logo192ApiCafe from '@/assets/images/logo192-apicafe.png';
import logoBankApi3x from '@/assets/images/logo-bankapi-3x.png';

export const getSubdomain = () => {
  if (env.dev.subdomain) {
    return env.dev.subdomain;
  }

  const { host } = window.location;
  const hostParts = host.split('.');
  if (hostParts.length < 3) {
    return undefined;
  }

  const subdomain = hostParts[1] ? hostParts[0] : undefined;
  return subdomain;
};

export const getRouteChildrenRoles = (
  router: Route[] = [],
  pathname: string,
): Route | undefined => {
  const targetRoute = router.find(
    ({ path, routes }) =>
      pathToRegexp(path).exec(pathname) || (routes && getRouteChildrenRoles(routes, pathname)),
  );

  return targetRoute;
};

export const getRouteForPath = (path: string, routes: Route[]) => {
  let targetRoute: Route | undefined;
  routes.forEach((route) => {
    if (pathToRegexp(`${route.path}/(.*)`).test(`${path}/`)) {
      if (route.roles) {
        targetRoute = route;
      }
      if (route.path === path) {
        targetRoute = route || targetRoute;
      }
      if (route.routes) {
        targetRoute = getRouteChildrenRoles(route.routes, path) || targetRoute;
      }
    }
  });
  return targetRoute;
};

export const getPageQueryParam = (param: string) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
};

export const getAuthUser = (): AuthUser | undefined => {
  try {
    const decodedToken: any = jwtDecode(authHelpers.getIdToken() ?? '');
    const { organisationId: organisation } = decodedToken;
    const organisationId = Array.isArray(organisation) ? organisation[0] : organisation;
    return {
      firstName: decodedToken.given_name ?? decodedToken['custom:firstName'],
      lastName: decodedToken.family_name ?? decodedToken['custom:lastName'],
      roles: decodedToken.groups ?? decodedToken.roles ?? decodedToken['cognito:groups'] ?? [],
      organisationId,
    };
  } catch {
    return undefined;
  }
};

export const urlSafe = (text: string) =>
  text
    .trim()
    .replace(/[^a-zA-Z0-9- ]/g, '') // remove invalid characters
    .replace(/\s\s+/g, ' ') // trim whitespace
    .replace(/ /g, '-') // replace space with -
    .toLowerCase();

export const searchString = (target: string | string[], searchValue: string) => {
  const searchKey = searchValue.toLowerCase();
  const searchTarget =
    target instanceof Array ? target.map((str) => str.toLowerCase()) : target.toLowerCase();
  const searchResult =
    searchTarget instanceof Array
      ? !!searchTarget.filter((str) => str.includes(searchKey)).length
      : searchTarget.includes(searchKey);
  return searchResult;
};

export const humanReadableTime = (time: string) => {
  const formatedTime = moment(moment.utc(time)).local();
  const duration = moment.duration(moment().diff(formatedTime));
  const seconds = duration.asSeconds();
  const humanizeDuration = moment.duration(-seconds, 'seconds').humanize(true);

  return humanizeDuration;
};

export const regex = {
  htmlTags: /(<([^>]+)>)/g,
  htmlTagsExcept: (tagName: string) => new RegExp(`\<(?!${tagName}).*?\>`, 'g'),
  htmlTagsInsensitive: /(<([^>]+)>)/i,
  lineBreak: /\\n/i,
  email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
  url: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\..+/i,
  digits: /^\d+$/i,
  phoneNumber: /(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?/i,
};

export const truncateString = (content: string, maxLength: number) => {
  const contentLength = content.length;
  return `${content.slice(0, contentLength > maxLength ? maxLength : contentLength)}${
    contentLength > maxLength ? '...' : ''
  }`;
};

export const truncateStringByWords = (content: string, maxWords: number) => {
  const contentSplited = (content || '').split(' ');
  if (maxWords >= contentSplited.length) {
    return content;
  }
  const contentSplitedOptimized = contentSplited.filter((word, index) => index < maxWords);
  const contentTruncated = contentSplitedOptimized.join(' ');
  return `${contentTruncated}...`;
};

export const beautifyLongText = (content: string, maxLength: number) => {
  return truncateString(
    content.replace(regex.htmlTags, '').replace(regex.lineBreak, ''),
    maxLength,
  );
};

export const organisationToSubdomain = (organisationName?: string) => {
  if (!organisationName || organisationName.trim().length === 0) return '';
  return organisationName
    .replace(/[^a-zA-Z0-9- ]/g, '') // strip off non-whitelisted characters
    .replace(/\s\s+/g, ' ') // trim whitespace
    .replace(/ /g, '-') // replace space with -
    .toLowerCase();
};

export const persistPageBeforeLogin = () => {
  const { pathname } = window.location;
  navigate('/auth/login', { state: { pathname } });
};

export const isInViewport = (elem: any) => {
  const bounding = elem.getBoundingClientRect();
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const LightenDarkenColor = (col: string, amt: number) => {
  let usePound = false;

  if (col[0] === '#') {
    col = col.slice(1);
    usePound = true;
  }

  const num = parseInt(col, 16);

  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
};

export const detectWebApp = {
  [SiteId.BankApi]: {
    shortName: 'Bank',
    appName: 'Bank-API',
    siteName: 'Bank-API.net',
    domainName: 'bank-api',
    headline: 'Open Banking as a Service',
    tld: '.net',
    logo: logoBankApi,
    logoNoText: logoBankApiNotext,
    favicon: logoBankApiNotext,
    logo192: logo192BankApi,
    coverImage: logoBankApi3x,
    metadata: {
      site: '@bank-api',
    },
    gdpr: {
      id: 'Cookiebot',
      src: 'https://consent.cookiebot.com/uc.js',
      dataCbid: 'c0fd613a-ade0-415e-822c-824d1e14ca05',
    },
  },
  [SiteId.ApiCafe]: {
    shortName: 'API Portal',
    appName: 'ApiCafe',
    domainName: 'apicafe',
    siteName: 'ApiCafe',
    headline: 'ApiCafe',
    tld: '.io',
    logo: logoApiCafe,
    logoNoText: logoApiCafeNotext,
    favicon: logoApiCafeNotext,
    logo192: logo192ApiCafe,
    coverImage: logo192ApiCafe,
    metadata: {
      site: '@apicafe',
    },
    gdpr: {
      id: 'Cookiebot',
      src: 'https://consent.cookiebot.com/uc.js',
      dataCbid: '29daab8d-e460-4b17-af32-255aaebf17eb',
    },
  },
};

export const generateScope = () => {
  let timestamp = Date.now().toString(); // Get current timestamp
  const remainingLength = 16 - timestamp.length; // Calculate remaining length for random digits
  let randomPart = '';

  if (remainingLength > 0) {
    randomPart = Math.floor(
      // eslint-disable-next-line no-restricted-properties
      Math.random() * Math.pow(10, remainingLength),
    ).toString(); // Generate random number
  } else if (remainingLength < 0) {
    timestamp = timestamp.slice(0, 16); // Trim timestamp if it's longer than 16 digits
  }

  const twelveDigitString = timestamp + randomPart.padStart(remainingLength, '0'); // Concatenate timestamp and random number
  return twelveDigitString;
};

export const getFibonacci = (n: number): number => {
  if (n <= 1) return n;
  let a = 0, b = 1, c;
  for (let i = 2; i <= n; i++) {
    c = a + b;
    a = b;
    b = c;
  }
  return b;
}